import { z } from "zod";
import type { CompletionItem } from "./completion-picker";
import { MEDICAL_EVIDENCE_COPY } from "./medical-evidence";

// TES-1201 (TES-1173 point 22, TES-1180 point 3; Emelie 2026-10-08, A-02
// "Läkarens patientbesked i admin") — the doctor's notice to the patient
// after a care meeting or a completion.
//
// It used to be "Återkoppling till patient · skickas i chatten", a doctor
// chat message. The doctor has no patient chat any more, so it is published
// as a permanent medical notice (kind `doctor_notice`, TES-1147) that she
// reads under Din vård, with the push and Home card that every notice gets.
//
// Her rules, which this file is the one place for:
//   - ONE coherent editable text, not six required fields. The headings may
//     be used in the body, and a heading with no relevant content is left
//     out (`finalizeDoctorNotice`).
//   - Prefilled from the doctor's decision, the chosen completions and the
//     CONFIRMED actions (`buildDoctorNoticePrefill`), so Kirill mainly
//     writes the individual medical assessment.
//   - "Beskedet får inte säga att remiss, provbeställning eller recept finns
//     innan motsvarande åtgärd har bekräftats i Webdoc."
//     (`doctorNoticeUnconfirmedClaims`, refused by every publish schema).

// Her label, verbatim.
export const DOCTOR_NOTICE_FIELD_LABEL =
  "Läkarens besked till patienten · visas under Din vård";

// The headings, verbatim and in her order.
export const DOCTOR_NOTICE_HEADINGS = [
  "Sammanfattning efter mötet",
  "Medicinsk bedömning",
  "Vad händer nu?",
  "Det här behöver du göra",
  "Det här gör Ella",
  "Övrig information till patienten",
] as const;
export type DoctorNoticeHeading = (typeof DOCTOR_NOTICE_HEADINGS)[number];

export const DOCTOR_NOTICE_BODY_MAX = 4000;
export const doctorNoticeBodySchema = z
  .string()
  .trim()
  .max(DOCTOR_NOTICE_BODY_MAX);

// The external actions a sentence may depend on, each true only once the
// doctor has confirmed it done in Webdoc. `labOrder` is the blood test's
// "Provbeställning/remiss"; `prescription` is "Receptet är utfärdat i
// Webdoc". No publish confirms a prescription today (the meeting never
// decides one, and the treatment decision only plans the order), so every
// caller passes false for it until such a confirmation exists.
export interface DoctorNoticeConfirmations {
  labOrder: boolean;
  prescription: boolean;
}
export type DoctorNoticeClaim = keyof DoctorNoticeConfirmations;

// The prefill's own sentences that state an action exists or ask her to act
// on it. Each is written only when its confirmation is there, and refused in
// a published notice when it is not.
const BLOOD_TEST_TASK_TITLE =
  MEDICAL_EVIDENCE_COPY.sv.tasks.new_blood_test.title;
export const DOCTOR_NOTICE_CLAIM_LINES: Record<DoctorNoticeClaim, string[]> = {
  labOrder: [
    "Provbeställningen (remissen) för ditt blodprov är skapad.",
    `– ${BLOOD_TEST_TASK_TITLE}`,
    // TES-1289 — A-02's line in "Sammanfattning efter vårdmötet".
    "Jag har skapat den elektroniska remissen för ditt blodprov.",
  ],
  prescription: ["Receptet är utfärdat."],
};

// The same claims in the doctor's own words: a remiss / lab order that is
// created, sent or done, or a prescription that is issued or sent. A clause
// with "inte" in it ("receptet är inte utfärdat ännu") is not a claim.
const CLAIM_PATTERNS: Record<DoctorNoticeClaim, RegExp[]> = {
  labOrder: [
    /\b(remiss|remissen|provbeställning|provbeställningen)\b[^.!?\n]*\b(skapad|skickad|klar|gjord|beställd|lagd)\b/i,
    // TES-1289 — the same claim in the first person, verb first ("Jag har
    // skapat remissen …").
    // "inte" right before the verb is kept in the match, so "Jag har inte
    // skapat remissen ännu" is not a claim.
    /(?:\binte\s+)?\b(skapat|skickat|gjort|beställt|lagt)\b[^.!?\n]*\b(remiss|remissen|provbeställning|provbeställningen)\b/i,
  ],
  prescription: [
    /\b(recept|receptet|e-recept|e-receptet)\b[^.!?\n]*\b(utfärdat|utfärdats|skickat|skickats|klart|förskrivet)\b/i,
  ],
};

/** What the notice says exists before its Webdoc confirmation, if anything. */
export function doctorNoticeUnconfirmedClaims(
  body: string,
  confirmed: DoctorNoticeConfirmations,
): DoctorNoticeClaim[] {
  const out: DoctorNoticeClaim[] = [];
  for (const claim of ["labOrder", "prescription"] as const) {
    if (confirmed[claim]) continue;
    const exact = DOCTOR_NOTICE_CLAIM_LINES[claim].some((line) =>
      body.includes(line),
    );
    const worded = CLAIM_PATTERNS[claim].some((re) => {
      const m = re.exec(body);
      return m !== null && !/\binte\b/i.test(m[0]);
    });
    if (exact || worded) out.push(claim);
  }
  return out;
}

/** The refinement message every publish schema uses for such a claim. */
export const DOCTOR_NOTICE_UNCONFIRMED_CLAIM =
  "doctor_notice_unconfirmed_claim";

// What the doctor reads when the notice is refused.
export const DOCTOR_NOTICE_CLAIM_ERROR: Record<DoctorNoticeClaim, string> = {
  labOrder:
    "Beskedet säger att provbeställningen/remissen finns, men den är inte bekräftad i Webdoc.",
  prescription:
    "Beskedet säger att receptet är utfärdat, men det är inte bekräftat i Webdoc.",
};

// --- the prefill -------------------------------------------------------------

export interface DoctorNoticePrefillInput {
  // Where the notice is written: after a care meeting, or with the treatment
  // decision after a completion. Only the meeting has "Sammanfattning efter
  // mötet".
  context: "meeting" | "decision";
  // The doctor's decision, in the vocabulary of the surface it is taken on:
  // the meeting's picker (proceed / completion / cannot_proceed) or the
  // treatment decision (approve / completion / decline).
  decision:
    | "proceed"
    | "completion"
    | "cannot_proceed"
    | "approve"
    | "decline"
    | null;
  // The chosen completions, on a completion decision.
  items: readonly CompletionItem[];
  // "Annan specificerad komplettering": the row's title, as she reads it.
  otherHeading?: string;
  confirmed: DoctorNoticeConfirmations;
}

// Our sentences around her headings. Where an approved line exists it is
// reused verbatim (CP-06's body, its waiting line, the blood test's
// reported note, the task titles); the rest is proposed copy, editable by
// the doctor before every publish, and listed in the PR for Emelie.
const LINE = {
  thanks: "Tack för vårdmötet.",
  proceed:
    "Läkaren bedömer att underlaget räcker för att gå vidare med din medicinska bedömning.",
  // CP-06 body, verbatim (first sentence).
  completion:
    "Läkaren behöver ytterligare information för att kunna fortsätta din medicinska bedömning eller uppföljning.",
  // CP-06's neutral waiting line, verbatim (second sentence).
  nextStepInApp: "Du får besked i appen när nästa steg är klart.",
  // Derived from CP-06's "När detta är klart" / "Läkaren kan gå vidare med
  // ditt ärende." (ours).
  whenDone: "När uppgifterna är klara kan läkaren gå vidare med ditt ärende.",
  // The blood test's reported note (TES-942), verbatim second sentence.
  afterBloodTest:
    "Din läkare återkommer med besked och behandlingsbeslut när provsvaret har kommit.",
  findTasks: "Du hittar uppgifterna i appen.",
  subscriptionLink: "Vi skickar en länk till abonnemanget till din e-post.",
} as const;

function taskLines(input: DoctorNoticePrefillInput): string[] {
  const titles = MEDICAL_EVIDENCE_COPY.sv.tasks;
  const out: string[] = [];
  for (const item of input.items) {
    if (item === "other") {
      if (input.otherHeading?.trim())
        out.push(`– ${input.otherHeading.trim()}`);
      continue;
    }
    // "Patienten får inte få uppgiften att lämna blodprov innan denna
    // bekräftelse finns" (A-02).
    if (item === "new_blood_test" && !input.confirmed.labOrder) continue;
    out.push(`– ${titles[item].title}`);
  }
  return out;
}

/** The notice's sections, each heading with its prefilled lines. */
export function doctorNoticePrefillSections(
  input: DoctorNoticePrefillInput,
): Array<{ heading: DoctorNoticeHeading; lines: string[] }> {
  const { decision, confirmed } = input;
  const completion = decision === "completion";
  const bloodTest =
    completion && input.items.includes("new_blood_test") && confirmed.labOrder;

  const summary: string[] = [];
  if (input.context === "meeting") {
    summary.push(LINE.thanks);
    if (decision === "proceed") summary.push(LINE.proceed);
    if (completion) summary.push(LINE.completion);
  } else if (completion) {
    summary.push(LINE.completion);
  }

  const next: string[] = [];
  if (completion) next.push(bloodTest ? LINE.afterBloodTest : LINE.whenDone);
  if (decision === "proceed") next.push(LINE.nextStepInApp);

  const tasks = completion ? taskLines(input) : [];
  if (tasks.length > 0) tasks.push(LINE.findTasks);

  const ella: string[] = [];
  if (bloodTest) ella.push(DOCTOR_NOTICE_CLAIM_LINES.labOrder[0]!);
  if (confirmed.prescription)
    ella.push(DOCTOR_NOTICE_CLAIM_LINES.prescription[0]!);
  if (decision === "approve") ella.push(LINE.subscriptionLink);

  // "Sammanfattning efter mötet" belongs to the meeting; after a completion
  // the summary lines sit under "Vad händer nu?" instead.
  const sections: Array<{ heading: DoctorNoticeHeading; lines: string[] }> = [];
  if (input.context === "meeting") {
    sections.push({ heading: "Sammanfattning efter mötet", lines: summary });
  } else {
    next.unshift(...summary);
  }
  // Always offered, empty: the part Kirill writes himself. Dropped at
  // publish if he leaves it empty.
  sections.push({ heading: "Medicinsk bedömning", lines: [] });
  // TES-1317 — after a meeting, "Vad händer nu?" is generated by the system
  // and appended at publish (`withWhatHappensNext`); the doctor's text must
  // not carry it too. The decision workspace keeps it until it gets the
  // generated block.
  if (input.context === "decision")
    sections.push({ heading: "Vad händer nu?", lines: next });
  sections.push({ heading: "Det här behöver du göra", lines: tasks });
  sections.push({ heading: "Det här gör Ella", lines: ella });
  return sections.filter(
    (s) => s.heading === "Medicinsk bedömning" || s.lines.length > 0,
  );
}

// Every sentence the prefill writes, which is never the doctor's own words.
const PREFILL_LINES: ReadonlySet<string> = new Set([
  ...Object.values(LINE),
  ...Object.values(DOCTOR_NOTICE_CLAIM_LINES).flat(),
  ...Object.values(MEDICAL_EVIDENCE_COPY.sv.tasks).map((t) => `– ${t.title}`),
]);

/**
 * TES-1400 (Ö-529, Emelie 2026-10-15) — what the doctor wrote himself: the
 * published text without its headings, the prefill's sentences and the task
 * rows. "Vid Kan inte gå vidare får patienten ett läkargodkänt besked med
 * personlig förklaring och rekommenderad väg framåt." A notice for that
 * outcome is refused while this is empty: the thanks alone, or the
 * generated "Vad händer nu?", is no explanation.
 */
export function doctorNoticePersonalText(body: string): string {
  return finalizeDoctorNotice(body)
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l && !HEADING_SET.has(l) && !PREFILL_LINES.has(l))
    .join("\n");
}

/** The prefilled notice text. */
export function buildDoctorNoticePrefill(
  input: DoctorNoticePrefillInput,
): string {
  return doctorNoticePrefillSections(input)
    .map((s) => [s.heading, ...s.lines].join("\n"))
    .join("\n\n");
}

const HEADING_SET = new Set<string>(DOCTOR_NOTICE_HEADINGS);

/**
 * The text as it is published: every heading with nothing under it is left
 * out, surrounding blank lines are trimmed, and runs of blank lines collapse
 * to one. An empty result means there is no notice to publish.
 */
export function finalizeDoctorNotice(body: string): string {
  const lines = body.replace(/\r\n?/g, "\n").split("\n");
  const blocks: string[][] = [[]];
  for (const line of lines) {
    if (HEADING_SET.has(line.trim())) blocks.push([line.trim()]);
    else blocks[blocks.length - 1]!.push(line);
  }
  const kept = blocks
    .map((block) => {
      const [first, ...rest] = block;
      const isHeading = first !== undefined && HEADING_SET.has(first);
      const content = (isHeading ? rest : block).join("\n").trim();
      if (!content) return "";
      return isHeading ? `${first}\n${content}` : content;
    })
    .filter(Boolean);
  return kept.join("\n\n").replace(/\n{3,}/g, "\n\n");
}
